import { useEffect, useState } from "react";
import { Avatar, Badge, Icon, Banner, Card, EmptyState, ListGroup, ListRow, Spinner, Timeline, TimelineItem } from "@vavr/ui";
import { strokeToSvg, type Stroke } from "@vavr/core";
import { api, blobData } from "../shared/api.ts";
import { navigate } from "../shared/router.tsx";
import { useAsync } from "../shared/useAsync.ts";
import { useBlobUrl } from "../shared/useBlobUrl.ts";
import { dateTime, eventLabel, fullName, time } from "../shared/format.ts";
import { G, useGuardian } from "./context.tsx";
import { adultName, byDay, sentence, type GuardianChild, type GuardianEvent, type GuardianSite } from "./status.ts";
import { EVENT_ICON, EVENT_TONE } from "./Home.tsx";

const eventsPath = (site: string) => `/api/v1/guardian/sites/${site}/events`;

export function HistoryView({ site, child, events }: { site: GuardianSite["site"]; child: GuardianChild; events: GuardianEvent[] }) {
  const mine = events.filter((e) => e.child_id === child.id);
  if (!mine.length) return <EmptyState icon="clock" title="No history yet" description={`Pickups and drop-offs for ${child.first} will show here.`} />;
  return (
    <div className="pmu-g-days">
      {byDay(mine, site.tz).map((g) => (
        <section key={g.key} className="pmu-g-day" aria-label={g.heading}>
          <h2 className="pmu-g-section">{g.heading}</h2>
          <div className="pmu-g-panel">
            <Timeline>
              {g.events.map((e) => {
                const href = `${G}/sites/${site.id}/events/${e.id}`;
                return (
                  <TimelineItem key={e.id} icon={EVENT_ICON[e.type] ?? "note"} tone={EVENT_TONE[e.type] ?? "neutral"} title={sentence(e, child.first, site.tz)}
                    href={href} onClick={(ev) => { ev.preventDefault(); navigate(href); }}>
                    {eventLabel(e.type, "Update")}
                  </TimelineItem>
                );
              })}
            </Timeline>
          </div>
        </section>
      ))}
    </div>
  );
}

export function History({ siteId, childId }: { siteId: string; childId: string }) {
  const { overview, version } = useGuardian();
  const s = overview.sites.find((x) => x.site.id === siteId);
  const child = s?.children.find((c) => c.id === childId);
  const events = useAsync(() => api<{ events: GuardianEvent[] }>("GET", eventsPath(siteId)), [siteId, version]);
  if (!s || !child) return <EmptyState title="Not found" description="This child is not linked to your account." />;
  return (
    <div className="pmu-stack">
      <div className="pmu-g-profile">
        <Avatar kind="child" size="lg" name={fullName(child)} />
        <div><h2 className="pmu-g-profile__name">{fullName(child)}</h2><p className="pmu-muted">{s.site.name}</p></div>
      </div>
      {events.loading ? <Spinner label="Loading history" /> : events.error ? <Banner tone="danger" title={events.error.message} /> : <HistoryView site={s.site} child={child} events={events.data!.events} />}
    </div>
  );
}

function Signature({ path }: { path: string }) {
  const [svg, setSvg] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    blobData(path).then((b) => b.text()).then((t) => { if (live) setSvg(strokeToSvg(JSON.parse(t) as Stroke)); }, () => undefined);
    return () => { live = false; };
  }, [path]);
  if (!svg) return null;
  return <img className="pmu-g-signature" src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`} alt="Signature of the adult" />;
}

export function EventRecordView({ site, child, e }: { site: GuardianSite["site"]; child: GuardianChild | undefined; e: GuardianEvent }) {
  const who = adultName(e);
  const photo = useBlobUrl(e.photo_sha256 ? `/api/v1/guardian/sites/${site.id}/blobs/${e.photo_sha256}` : null);
  const tone = EVENT_TONE[e.type] ?? "neutral";
  return (
    <div className="pmu-stack pmu-g-record">
      <header className={`pmu-g-record__head pmu-g-record__head--${tone}`}>
        <span className="pmu-g-record__icon" aria-hidden="true"><Icon name={EVENT_ICON[e.type] ?? "note"} /></span>
        <h2 className="pmu-g-record__title">{eventLabel(e.type)}</h2>
        <p className="pmu-g-record__line">{sentence(e, child?.first ?? "Your child", site.tz)}</p>
      </header>
      <ListGroup>
        <ListRow leading={{ icon: "child", tone: "neutral" }} title={child ? fullName(child) : "—"} subtitle="Child" />
        {who && <ListRow leading={{ icon: "user", tone: "neutral" }} title={who} subtitle="Adult" />}
        <ListRow leading={{ icon: "clock", tone: "neutral" }} title={dateTime(e.server_ts, site.tz)} subtitle="Time" />
        <ListRow leading={{ icon: "door", tone: "neutral" }} title={site.name} subtitle="Where" />
      </ListGroup>
      {e.photo_sha256 && (
        <Card title="Photo taken at the door" description="Shown to you because you added this adult.">
          {photo ? <img className="pmu-g-photo" src={photo} alt={`Photo of ${who ?? "the adult"} at ${time(e.server_ts, site.tz)}`} /> : <Spinner label="Loading photo" />}
        </Card>
      )}
      {e.stroke_sha256 && <Card title="Signature"><Signature path={`/api/v1/guardian/sites/${site.id}/blobs/${e.stroke_sha256}`} /></Card>}
      <Card title="Proof" description="This record is sealed into your school's tamper-evident log. Anyone can check it." actions={<Badge tone="brand" icon="shield">{`Sealed #${e.seq}`}</Badge>}>
        <p className="pmu-small pmu-muted">Record fingerprint</p>
        <p className="pmu-mono pmu-small pmu-g-hash">{e.event_hash}</p>
      </Card>
    </div>
  );
}

export function EventRecord({ siteId, eventId }: { siteId: string; eventId: string }) {
  const { overview } = useGuardian();
  const s = overview.sites.find((x) => x.site.id === siteId);
  const inRecent = s?.recent.find((e) => e.id === eventId);
  const fetched = useAsync(() => (inRecent ? Promise.resolve({ events: [inRecent] }) : api<{ events: GuardianEvent[] }>("GET", eventsPath(siteId))), [siteId, eventId]);
  if (!s) return <EmptyState title="Not found" description="This record is not available to your account." />;
  if (fetched.loading && !fetched.data) return <Spinner label="Loading the record" />;
  const e = fetched.data?.events.find((x) => x.id === eventId);
  if (!e) return <EmptyState title="Not found" description="This record is not available to your account." />;
  return <EventRecordView site={s.site} child={s.children.find((c) => c.id === e.child_id)} e={e} />;
}
